import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Compare as CompareIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import FilterToggleButton from '../../components/map/FilterToggleButton';

// SCENARIO_TYPES and defaultScenario should match the logic from ScenarioBuilder.tsx
const SCENARIO_TYPES = {
  'Market Expansion': {
    metrics: {
      netPatientRevenue: { label: 'Target Revenue Growth (%)', min: -50, max: 100 },
      marketShare: { label: 'Target Market Share (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Expected Leakage Reduction (%)', min: -50, max: 0 },
      waitTimes: { label: 'Target Wait Time Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Target Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Expected Cost Increase (%)', min: 0, max: 100 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 },
      coveredLives: { label: 'Target Covered Lives Increase (%)', min: 0, max: 100 }
    }
  },
  'Leakage Reduction': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Recovery (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction Target (%)', min: -50, max: 0 },
      waitTimes: { label: 'Wait Time Improvement (%)', min: -50, max: 0 },
      readmissionRates: { label: 'Readmission Rate Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Cost Reduction (%)', min: -50, max: 0 },
      coveredLives: { label: 'Patient Retention Increase (%)', min: 0, max: 100 }
    }
  },
  'Service/Site Relocation': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      waitTimes: { label: 'Wait Time Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: -10000000, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'Hiring/Recruitment': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Growth (%)', min: 0, max: 100 },
      marketShare: { label: 'Market Share Growth (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction (%)', min: -50, max: 0 },
      waitTimes: { label: 'Wait Time Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Cost Increase (%)', min: 0, max: 100 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'M&A': {
    metrics: {
      netPatientRevenue: { label: 'Combined Revenue Growth (%)', min: 0, max: 100 },
      marketShare: { label: 'Combined Market Share (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction (%)', min: -50, max: 0 },
      expenses: { label: 'Cost Synergy (%)', min: -50, max: 0 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'Payor Network Optimization': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 },
      coveredLives: { label: 'Covered Lives Change (%)', min: -50, max: 100 }
    }
  },
  'Patient Marketing': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Growth (%)', min: 0, max: 100 },
      marketShare: { label: 'Market Share Growth (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Marketing Cost (%)', min: 0, max: 100 },
      coveredLives: { label: 'New Patient Acquisition (%)', min: 0, max: 100 }
    }
  },
  'Operations Optimization': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      waitTimes: { label: 'Wait Time Reduction (%)', min: -50, max: 0 },
      readmissionRates: { label: 'Readmission Rate Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Cost Reduction (%)', min: -50, max: 0 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'Competitive Response': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      careScores: { label: 'Care Score Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 }
    }
  },
  'Comprehensive Strategy': {
    metrics: {
      netPatientRevenue: { label: 'Overall Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      waitTimes: { label: 'Wait Time Impact (%)', min: -50, max: 50 },
      readmissionRates: { label: 'Readmission Rate Impact (%)', min: -50, max: 50 },
      careScores: { label: 'Care Score Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 }
    }
  }
};

const defaultScenario = {
  id: '1',
  name: 'Market Expansion - Cardiac Care',
  type: 'Market Expansion',
  status: 'draft',
  parameters: {
    timeframe: {
      lookbackPeriod: 12,
      startDate: new Date(),
      completionDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000),
      projectLife: 60
    },
    budget: {
      min: 1000000,
      max: 5000000
    },
    metrics: {
      netPatientRevenue: 15,
      marketShare: 25,
      patientLeakage: -10,
      waitTimes: -20,
      readmissionRates: -5,
      careScores: 10,
      expenses: -15,
      irr: 20,
      npv: 3000000,
      paybackPeriod: 36,
      baselineCosts: -10,
      coveredLives: 15
    },
    filters: {
      facilities: [],
      providers: [],
      geography: {
        type: 'zip',
        value: ''
      },
      specialties: ['Cardiology'],
      patientCohorts: [],
      organizations: []
    }
  }
};

// Define the allowed metric keys type at the top of the file (if not already defined)
type MetricKey = keyof typeof defaultScenario.parameters.metrics;

const ScenarioBuilderTray = ({ onClose }: { onClose: () => void }) => {
  const [selectedScenario, setSelectedScenario] = useState(defaultScenario);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const handleMetricChange = (metric: string, value: number) => {
    setSelectedScenario(s => ({
      ...s,
      parameters: {
        ...s.parameters,
        metrics: {
          ...s.parameters.metrics,
          [metric]: value
        }
      }
    }));
  };

  const scenarioMetrics = SCENARIO_TYPES[selectedScenario.type as keyof typeof SCENARIO_TYPES]?.metrics || {};

  return (
    <Box className="scenario-tray adaptive-scenario-tray">
      <Box className="scenario-tray-header">
        <Typography variant="h6">Scenario Builder Controls</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className="scenario-tray-controls">
        <FilterToggleButton showFilters={showFilters} onToggle={() => setShowFilters(!showFilters)} />
        <Button variant="contained" size="small" sx={{ ml: 1 }}>New Scenario</Button>
      </Box>
      <Stack spacing={2} className="scenario-tray-stack" sx={{overflowY: 'auto'}}>
        {showFilters && (
          <>
            {/* Scenario Type Controls */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Scenario Type</InputLabel>
                  <Select
                    value={selectedScenario.type}
                    onChange={e => setSelectedScenario(s => ({ ...s, type: e.target.value }))}
                    label="Scenario Type"
                  >
                    {Object.keys(SCENARIO_TYPES).map(type => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Save current scenario">
                  <IconButton color="primary" size="small">
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Compare scenarios">
                  <IconButton color="primary" size="small">
                    <CompareIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            {/* Timeframe Controls */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
                  <TimelineIcon fontSize="small" color="primary" />
                  Timeframe Parameters
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Lookback Period (months)
                    </Typography>
                    <Slider
                      value={selectedScenario.parameters.timeframe.lookbackPeriod}
                      onChange={(_, value) => setSelectedScenario(s => ({ ...s, parameters: { ...s.parameters, timeframe: { ...s.parameters.timeframe, lookbackPeriod: value as number } } }))}
                      min={1}
                      max={60}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Project Life (months)
                    </Typography>
                    <Slider
                      value={selectedScenario.parameters.timeframe.projectLife}
                      onChange={(_, value) => setSelectedScenario(s => ({ ...s, parameters: { ...s.parameters, timeframe: { ...s.parameters.timeframe, projectLife: value as number } } }))}
                      min={1}
                      max={120}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
            {/* Budget Controls */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
                  <FilterIcon fontSize="small" color="primary" />
                  Budget Parameters
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      Budget Range ($)
                    </Typography>
                    <Slider
                      value={[selectedScenario.parameters.budget.min, selectedScenario.parameters.budget.max]}
                      onChange={(_, value) => setSelectedScenario(s => ({ ...s, parameters: { ...s.parameters, budget: { min: (value as number[])[0], max: (value as number[])[1] } } }))}
                      min={0}
                      max={10000000}
                      valueLabelDisplay="auto"
                      valueLabelFormat={value => `$${value.toLocaleString()}`}
                    />
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
            {/* Metrics Controls */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500, fontSize: '0.875rem' }}>
                  <FilterIcon fontSize="small" color="primary" />
                  Key Evaluation Metrics
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {Object.entries(scenarioMetrics).map(([key, config]) => (
                    <Box key={key}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {config.label}
                      </Typography>
                      <Slider
                        value={selectedScenario.parameters.metrics[key as MetricKey]}
                        onChange={(_, value) => handleMetricChange(key as MetricKey, value as number)}
                        min={config.min}
                        max={config.max}
                        valueLabelDisplay="auto"
                        valueLabelFormat={value => key === 'npv' ? `$${value.toLocaleString()}` : `${value}%`}
                      />
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
            {/* Map Controls */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormControlLabel
                  control={<Switch size="small" />}
                  label="Show Heatmap"
                />
                <Tooltip title="Play/Pause Simulation">
                  <IconButton size="small" onClick={() => setIsPlaying(p => !p)}>
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Timeline">
                  <IconButton size="small">
                    <TimelineIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ScenarioBuilderTray;
